import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import Loader from '../../Shared/Loader/Loader';
import APIConstant from '../../../utils/pathConstants';
import api from '../../../utils/apiClient';
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import DeleteConfirmation from '../../Dialogs/DeleteConfirmation';
import AddEditThemeMaster from './AddEditThemeMaster';
import { useCatchResponse } from '../../../utils/useCatchResponse';
import { useTheme } from '../../../context/ThemeContext';

const ThemeMaster = () => {
  const { catchResponse } = useCatchResponse();
  const { reloadTheme, activeThemeId } = useTheme();
  const [themeList, setThemeList] = useState([]);
  const [masterData, setMasterData] = useState({});
  const [themeId, setThemeId] = useState('');
  const [visible, setVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ThemeName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [securityUserID, setSecurityUserID] = useState('');
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  useEffect(() => {
    const loginData = CommonConfig.loginData();
    setSecurityUserID(loginData?._id);
    GetThemeList(loginData?._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const GetThemeList = async (id) => {
    try {
      const data = { UserID: id };
      await api
        .post(APIConstant.path.getCustomThemeList, data)
        .then((response) => {
          if (response.success) {
            setThemeList(response.data || []);
            Loader.hide();
          }
        })
        .catch((err) => {
          Loader.hide();
          catchResponse(err);
        });
    } catch (err) {
      catchResponse(err);
    }
  };

  const SetActive = async (row) => {
    try {
      Loader.show();
      await api
        .post(APIConstant.path.setActiveTheme, {
          UserID: securityUserID,
          ThemeId: row._id,
        })
        .then(async (res) => {
          if (res.success) {
            Toast.success({ message: res.message });
            await reloadTheme();
          } else {
            Toast.error({ message: res.message });
          }
          Loader.hide();
        })
        .catch((err) => {
          Loader.hide();
          catchResponse(err);
        });
    } catch (err) {
      Loader.hide();
      catchResponse(err);
    }
  };

  const DeleteTheme = async () => {
    try {
      Loader.show();
      await api
        .post(APIConstant.path.deleteThemeMaster, {
          id: deleteId,
          DeletedBy: securityUserID,
        })
        .then(async (res) => {
          if (res.success) {
            Toast.success({ message: 'Deleted successfully' });
            if (deleteId === activeThemeId) await reloadTheme();
          } else {
            Toast.error({ message: res.message || res.error });
          }
          Loader.hide();
          setDeleteId('');
          handleClose();
          GetThemeList(securityUserID);
        })
        .catch((err) => {
          Loader.hide();
          setDeleteId('');
          handleClose();
          catchResponse(err);
        });
    } catch (err) {
      catchResponse(err);
    }
  };

  const onCancel = () => {
    setVisible(false);
    setThemeId('');
    setIsEdit(false);
    setMasterData({});
    GetThemeList(securityUserID);
  };

  const renderFooter = () => (
    <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
      <Button
        label="Cancel"
        className="btn-text-action-secondary"
        onClick={handleClose}
      />
      <Button
        label="Delete"
        className="btn-text-action-primary"
        onClick={() => DeleteTheme()}
      />
    </div>
  );

  const handleClose = () => setIsDeleteConfirmation(false);

  const colorSwatch = (hex) => (
    <span
      title={hex}
      style={{
        display: 'inline-block',
        width: 18,
        height: 18,
        borderRadius: 4,
        background: hex || '#fff',
        border: '1px solid #ddd',
        marginRight: 4,
      }}
    />
  );

  const previewBodyTemplate = (row) => {
    const c = row.Colors || {};
    return (
      <div className="p-d-flex p-ai-center">
        {colorSwatch(c.buttonBg)}
        {colorSwatch(c.buttonText)}
        {colorSwatch(c.buttonHover)}
        {colorSwatch(c.sidebarBg)}
        {colorSwatch(c.sidebarText)}
        {colorSwatch(c.headerBg)}
        {colorSwatch(c.headerText)}
      </div>
    );
  };

  const activeBodyTemplate = (row) =>
    row._id === activeThemeId ? (
      <Tag severity="success" value="Active" />
    ) : (
      <Button
        label="Set as Active"
        className="p-button-sm"
        onClick={() => SetActive(row)}
      />
    );

  const actionBodyTemplate = (row) => (
    <React.Fragment>
      <div className="p-d-flex p-ai-center p-jc-end">
        <Button
          icon="pi pi-pencil"
          className="e-sction-btn p-m-0"
          onClick={() => {
            setThemeId(row._id);
            setMasterData(row);
            setIsEdit(true);
            setVisible(true);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="e-sction-btn p-m-0 p-m-2"
          onClick={() => {
            setIsDeleteConfirmation(true);
            setDeleteId(row._id);
          }}
        />
      </div>
    </React.Fragment>
  );

  const actionBodyTemplateSrNo = (data, props) => props.rowIndex + 1;

  return (
    <div className="wayment-owner-dashboard et-master-page">
      <div className="heading-area p-d-flex p-ai-center p-jc-between">
        <div className="p-d-flex p-ai-baseline">
          <i className="pi pi-palette"></i>
          <h2>Theme Master</h2>
        </div>
        <div className="p-d-flex p-ai-center p-jc-between">
          <div className="searchbar-area e-searchbar-area">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Global Search"
            />
          </div>
          <Button
            icon="pi pi-plus"
            className="e-h-btn"
            onClick={() => setVisible(true)}
          />
        </div>
      </div>
      <DataTable
        value={themeList}
        paginator
        rows={rows}
        first={first}
        onPage={onPageChange}
        filters={filters}
        globalFilterFields={['ThemeName']}
        responsiveLayout="scroll"
        paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
        rowsPerPageOptions={[5, 10, 20]}
        currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
        className='custom-table'
        scrollHeight='calc(100vh - 120px)'
        size='small'
      >
        <Column field="id" body={actionBodyTemplateSrNo} header="Sr No." />
        <Column field="ThemeName" header="Theme Name" />
        <Column body={previewBodyTemplate} header="Preview" />
        <Column body={activeBodyTemplate} header="Active" />
        <Column body={actionBodyTemplate} header="Action" />
      </DataTable>
      <Dialog
        header={themeId ? 'Edit Theme Master' : 'Add Theme Master'}
        className="dialog-popup"
        visible={visible}
        onHide={() => onCancel()}
        draggable={false}
        position="center"
        style={{ width: '650px' }}
      >
        <AddEditThemeMaster
          masterData={masterData}
          isEdit={isEdit}
          ID={themeId}
          onCancel={onCancel}
        />
      </Dialog>
      <Dialog
        visible={isDeleteConfirmation}
        onHide={handleClose}
        header={
          <div className="p-d-flex p-jc-between p-ai-center">
            <span>Delete Confirmation</span>
          </div>
        }
        footer={renderFooter()}
        draggable={false}
        dismissableMask
        position="center"
        style={{ width: '450px' }}
      >
        <DeleteConfirmation />
      </Dialog>
    </div>
  );
};

export default ThemeMaster;
